import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
    COMPUTE_REPLAY_METRICS,
    ComputeReplayMetricsResponse,
    ComputeReplayMetricsVariables,
    ReplayMetrics,
} from '../graphql/computeReplayMetrics';
import {
    SAVE_REPLAY_MANUAL_VERSION,
    SaveReplayManualVersionResponse,
    SaveReplayManualVersionVariables,
} from '../graphql/saveReplayManualVersion';
import {
    LIST_REPLAY_MANUAL_VERSIONS,
    ListReplayManualVersionsResponse,
    ListReplayManualVersionsVariables,
} from '../graphql/listReplayManualVersions';
import {
    FETCH_SAVED_VERSION_TRAJECTORY,
    FetchSavedVersionTrajectoryResponse,
    FetchSavedVersionTrajectoryVariables,
} from '../graphql/fetchSavedVersionTrajectory';
import {
    DELETE_REPLAY_MANUAL_VERSION,
    DeleteReplayManualVersionResponse,
    DeleteReplayManualVersionVariables,
} from '../graphql/deleteReplayManualVersion';
import { Point, ReplayStop } from '../graphql/types';
import { ReplayRouteDetail } from '../graphql/getReplayRouteDetail';
import { buildOrderedStops } from '../utils/metricsFormat';

/** 顺序指纹：有序 stop id 拼接，唯一标识一条路线（→ 一条轨迹 / 一组指标）。 */
const orderKey = (order: ReplayStop[]) => order.map((s) => s.stop).join(',');

interface TrajCacheEntry {
    metrics?: ReplayMetrics | null;
    trajectory?: Point[] | null; // undefined=未取；null=取过但空
}

/**
 * 指标 / 轨迹编排 + 会话级前端缓存（key = 顺序指纹 orderKey）：
 * - 算法预估（G5，算法序）：进详情自动算一次，指标 + 轨迹一并缓存（导航档看算法路线直接命中）
 * - 手动结果（computeManual，G5）：按需算，缓存 {指标, 轨迹}
 * - 版本轨迹（getTrajectory→G8）：导航档载入版本时按需取，缓存
 * - 保存（saveManual，G6）：轨迹从缓存取（保存前必先「查看本次结果」已缓存）落库
 * - 版本列表（G7）：供策略对比
 * 缓存换线路(dspTaskId)清空，退出页面组件卸载自然回收。
 */
export function useReplayMetrics(detail: ReplayRouteDetail | null) {
    const client = useApolloClient();
    const warehouseId = detail?.route.warehouseId ?? null;
    // 版本按子路线 dspTaskId 存/查（G6/G7）
    const dspTaskId = detail?.route.dspTaskId ?? null;

    // orderKey → {指标, 轨迹}，会话级前端内存缓存（非 Redis）
    const cacheRef = useRef<Map<string, TrajCacheEntry>>(new Map());
    const patchCache = useCallback((key: string, patch: TrajCacheEntry) => {
        cacheRef.current.set(key, { ...cacheRef.current.get(key), ...patch });
    }, []);

    // 换线路：清空缓存（不同线路 stop 集合不同，key 不会串，但仍清空避免无界增长）
    useEffect(() => {
        cacheRef.current.clear();
    }, [dspTaskId]);

    // 算法预估（算法顺序，G5）——响应含指标 + 轨迹
    const { data: algoData, loading: algoLoading } = useQuery<ComputeReplayMetricsResponse, ComputeReplayMetricsVariables>(
        COMPUTE_REPLAY_METRICS,
        {
            variables: {
                input: {
                    warehouseId: warehouseId as number,
                    stops: buildOrderedStops(detail, detail?.stops ?? []),
                },
            },
            skip: detail == null || warehouseId == null,
            fetchPolicy: 'cache-first',
        }
    );
    const algorithmMetrics = algoData?.computeReplayMetrics ?? null;

    // 算法结果到手：把算法序的 {指标, 轨迹} 顺手塞缓存（导航档看算法路线零请求）
    useEffect(() => {
        if (detail && algorithmMetrics) {
            patchCache(orderKey(detail.stops), {
                metrics: algorithmMetrics,
                trajectory: algorithmMetrics.navigationTrajectory ?? null,
            });
        }
    }, [detail, algorithmMetrics, patchCache]);

    // 手动结果按需算（G5）：先查缓存，缺失才请求；结果连指标带轨迹缓存
    const computeManual = useCallback(
        async (order: ReplayStop[]): Promise<ReplayMetrics | null> => {
            if (warehouseId == null) return null;
            const key = orderKey(order);
            const cached = cacheRef.current.get(key);
            if (cached?.metrics != null) return cached.metrics;
            const { data } = await client.query<ComputeReplayMetricsResponse, ComputeReplayMetricsVariables>({
                query: COMPUTE_REPLAY_METRICS,
                variables: { input: { warehouseId, stops: buildOrderedStops(detail, order) } },
                fetchPolicy: 'network-only',
            });
            const m = data?.computeReplayMetrics ?? null;
            if (m) patchCache(key, { metrics: m, trajectory: m.navigationTrajectory ?? null });
            return m;
        },
        [client, detail, warehouseId, patchCache]
    );

    // 已保存版本列表（G7，策略对比）——只回指标，不含轨迹
    const { data: versionsData, refetch: refetchVersions } = useQuery<
        ListReplayManualVersionsResponse,
        ListReplayManualVersionsVariables
    >(LIST_REPLAY_MANUAL_VERSIONS, {
        variables: { dspTaskId: dspTaskId as number },
        skip: dspTaskId == null,
        fetchPolicy: 'cache-and-network',
    });
    const savedVersions = versionsData?.listReplayManualVersions ?? [];

    // 保存手动版本（G6）：轨迹从缓存按 orderKey 取（保存前必先「查看本次结果」→computeManual 已缓存该序）
    const [saveMutation, { loading: saving }] = useMutation<
        SaveReplayManualVersionResponse,
        SaveReplayManualVersionVariables
    >(SAVE_REPLAY_MANUAL_VERSION);
    const saveManual = useCallback(
        async (order: ReplayStop[], metrics: ReplayMetrics): Promise<boolean> => {
            if (dspTaskId == null) return false;
            // 轨迹点来自查询结果，Apollo 会塞 __typename；显式映射成 {lat,lng} 剥掉，否则 ReplayPointInput 报未知字段
            const cachedTraj = cacheRef.current.get(orderKey(order))?.trajectory ?? null;
            const trajectory = cachedTraj?.length ? cachedTraj.map((p) => ({ lat: p.lat, lng: p.lng })) : null;
            const { data } = await saveMutation({
                variables: {
                    input: {
                        dspTaskId,
                        warehouseId,
                        areaId: detail?.route.areaId ?? null,
                        orderedStops: buildOrderedStops(detail, order),
                        totalDistanceKm: metrics.totalDistanceKm ?? null,
                        totalDurationMinutes: metrics.totalDurationMinutes ?? null,
                        leftTurnCount: metrics.leftTurnCount ?? null,
                        uTurnCount: metrics.uTurnCount ?? null,
                        trafficLightCount: metrics.trafficLightCount ?? null,
                        navigationTrajectory: trajectory,
                    },
                },
            });
            const ok = data?.saveReplayManualVersion ?? false;
            if (ok) await refetchVersions();
            return ok;
        },
        [saveMutation, refetchVersions, detail, dspTaskId, warehouseId]
    );

    // 删除手动版本（逻辑删）：成功后刷新版本列表
    const [deleteMutation, { loading: deleting }] = useMutation<
        DeleteReplayManualVersionResponse,
        DeleteReplayManualVersionVariables
    >(DELETE_REPLAY_MANUAL_VERSION);
    const deleteManual = useCallback(
        async (versionId: number): Promise<boolean> => {
            const { data } = await deleteMutation({ variables: { versionId } });
            const ok = data?.deleteReplayManualVersion ?? false;
            if (ok) await refetchVersions();
            return ok;
        },
        [deleteMutation, refetchVersions]
    );

    // 已保存版本导航轨迹（G8，读库返回）
    const [fetchTrajMutation] = useMutation<
        FetchSavedVersionTrajectoryResponse,
        FetchSavedVersionTrajectoryVariables
    >(FETCH_SAVED_VERSION_TRAJECTORY);

    // 统一轨迹入口（供地图导航档调）：orderKey 命中→直接用；缺失→载入版本走 G8、否则 G5 算；结果缓存
    const getTrajectory = useCallback(
        async (order: ReplayStop[], versionId?: number | null): Promise<Point[] | null> => {
            const key = orderKey(order);
            const cached = cacheRef.current.get(key);
            if (cached && cached.trajectory !== undefined) return cached.trajectory ?? null;
            if (versionId != null) {
                const { data } = await fetchTrajMutation({ variables: { versionId } });
                const traj = data?.fetchSavedVersionTrajectory ?? null;
                patchCache(key, { trajectory: traj });
                return traj;
            }
            const m = await computeManual(order);
            return m?.navigationTrajectory ?? null;
        },
        [fetchTrajMutation, computeManual, patchCache]
    );

    return {
        algorithmMetrics,
        algorithmLoading: algoLoading,
        computeManual,
        getTrajectory,
        savedVersions,
        refetchVersions,
        saveManual,
        saving,
        deleteManual,
        deleting,
    };
}
